import { Component, inject, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { BookingService } from '@markant-platform/cartridge/data-access';
import { AuthenticatedUserService } from '@markant-platform/shared/user/data-access';
import { map, of, Subject, take, tap } from 'rxjs';
import { OrganizationService } from '@markant-platform/shared/organization/data-access';
import { BookingData, BookingState } from '@markant-platform/cartridge/model';

type BannerAlertUserRole = 'SERVICE_MANAGER_ECS' | 'SERVICE_MANAGER' | null;

@Component({
  selector: 'markant-platform-widget-alert',
  templateUrl: './widgetAlert.component.html',
  styleUrls: ['./widgetAlert.component.scss'],
  standalone: true,
})
export class WidgetAlertComponent implements OnInit, OnChanges, OnDestroy {
  private router = inject(Router);
  private translocoService = inject(TranslocoService);
  private bookingService = inject(BookingService);
  private authenticatedUserService = inject(AuthenticatedUserService);
  private organizationService = inject(OrganizationService);

  @Input() variantId?: string;

  warningBannerWidget = false;
  cartridgeId = '';
  userRole: BannerAlertUserRole = null;
  labelsAlertBanner: { text: string; link: string } | null = null;
  serviceManager = '';
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadWidgetData();
  }

  ngOnChanges(): void {
    this.loadWidgetData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadWidgetData(): void {
    if (!this.variantId) {
      return;
    }

    this.bookingService
      .getLatestBookingByVariantId(this.variantId, [BookingState.BOOKED, BookingState.UPGRADED])
      .pipe(
        take(1),
        tap(booking => this.handleBookingResponse(booking))
      )
      .subscribe();
  }

  handleBookingResponse(booking: BookingData | undefined): void {
    if (!booking) return;
    this.setAlertBannerLabels(booking.responsibleUserId);
    this.serviceManager = `${booking.responsibleUserFirstname} ${booking.responsibleUserLastname}`;
    this.cartridgeId = booking.cartridgeId;
    this.warningBannerWidget = booking.serviceUpgradeState === 'UPGRADE_SUGGESTED';
  }

  setAlertBannerLabels(responsibleUserId?: string): void {
    const roleTextKey$ =
      responsibleUserId === this.authenticatedUserService.userId
        ? this.organizationService
            .getCurrentOrganization()
            .pipe(map(org => (org?.ezrCustomer ? 'managerECS' : 'managerThirdParty')))
        : of('other');

    roleTextKey$.pipe(take(1)).subscribe(roleTextKey => {
      if (roleTextKey !== 'other') {
        this.userRole = 'SERVICE_MANAGER';
      }
      this.labelsAlertBanner = {
        text: this.translocoService.translate(`platformWidget.alertBanner.text.${roleTextKey}`),
        link: this.translocoService.translate(`platformWidget.alertBanner.link.${roleTextKey}`, {
          serviceManager: this.serviceManager,
        }),
      };
    });
  }

  async onAlertBannerNavigate(): Promise<void> {
    try {
      await this.router.navigateByUrl(`/cartridge/catalog/${this.cartridgeId}/detail`);
    } catch (error) {
      console.error('Navigation to the catalog failed:', error);
    }
  }
}
